import {
  fetchNotificationEvents,
  markAllNotificationsReadOnServer,
  markNotificationReadOnServer,
  sortNotificationsByTime,
} from '../services/notificationInbox'
import { fetchVapidPublicKey, isPushSupported, subscribeToWebPush } from '../services/pushNotifications'
import {
  type NotificationReadContext,
  type NotificationReadVia,
  buildNotificationReadContext,
} from '../utils/notificationClient'
import { notificationGroupKey } from '../utils/notificationDisplay'
import { VENDOR, type VendorCode } from '../utils/vendorDisplayName'
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import { useNavigate } from 'react-router-dom'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'

export type FmsNotificationPayload = {
  id: number
  event_type: string
  title: string
  body: string
  severity: 'info' | 'warning' | 'error' | string
  read?: boolean
  payload?: { deep_link?: string; [key: string]: unknown }
  robot_id?: number | null
  robot_name?: string | null
  robot_serial?: string | null
  robot_color?: string | null
  station_pk?: number | null
  station_id?: string | null
  station_name?: string | null
  odyssey_id?: number | null
  created_at?: string
}

export type FmsNotificationInboxItem = FmsNotificationPayload & {
  read: boolean
  /** False after auto-hide timer; item stays in inbox as unread until read. */
  toastVisible: boolean
}

/** Max toast banners shown at once; inbox in the bell is not capped to this. */
export const MAX_CONCURRENT_TOASTS = 8
/** Safety cap for in-memory inbox (history loaded from API on mount). */
export const MAX_INBOX_ITEMS = 100
const TOAST_VISIBLE_MS = 10_000

function capConcurrentToasts(items: FmsNotificationInboxItem[]): FmsNotificationInboxItem[] {
  let visible = 0
  return items.map(item => {
    if (!item.toastVisible) {
      return item
    }
    visible += 1
    if (visible > MAX_CONCURRENT_TOASTS) {
      return { ...item, toastVisible: false }
    }
    return item
  })
}

function toInboxItem(data: FmsNotificationPayload, toastVisible: boolean): FmsNotificationInboxItem {
  return {
    ...data,
    read: Boolean(data.read),
    toastVisible,
  }
}

export function mergeInboxItems(
  prev: FmsNotificationInboxItem[],
  incoming: FmsNotificationInboxItem[]
): FmsNotificationInboxItem[] {
  const byId = new Map<number, FmsNotificationInboxItem>()
  for (const item of incoming) {
    byId.set(item.id, item)
  }
  for (const item of prev) {
    const existing = byId.get(item.id)
    if (!existing) {
      byId.set(item.id, item)
      continue
    }
    byId.set(item.id, {
      ...existing,
      toastVisible: existing.toastVisible || item.toastVisible,
      read: existing.read || item.read,
    })
  }
  return capConcurrentToasts(Array.from(byId.values()).sort(sortNotificationsByTime).slice(0, MAX_INBOX_ITEMS))
}

function upsertItem(
  prev: FmsNotificationInboxItem[],
  data: FmsNotificationPayload,
  toastVisible: boolean
): FmsNotificationInboxItem[] {
  const next = toInboxItem(data, toastVisible)
  return mergeInboxItems(prev, [next])
}

function isOdysseyNotificationEvent(eventType: string): boolean {
  return eventType.startsWith('odyssey.')
}

function resolveNotificationPath(item: FmsNotificationInboxItem): string | null {
  const link = item.payload?.deep_link
  if (typeof link === 'string' && link.startsWith('/')) {
    return link
  }
  if (!item.robot_id) {
    return null
  }
  if (isOdysseyNotificationEvent(item.event_type)) {
    return `/robot/${item.robot_id}`
  }
  if (item.event_type.startsWith('gausium.')) {
    const vendor = item.payload?.robot_vendor as VendorCode | undefined
    if (vendor === VENDOR.GAUSIUM) {
      return `/mission-control/${item.robot_id}`
    }
    return `/robot/${item.robot_id}`
  }
  return `/robot/${item.robot_id}`
}

export type WebPushUiState =
  | 'unsupported'
  | 'denied'
  | 'prompt' // supported + VAPID; user must allow (typical on iOS PWA)
  | 'enabled'

const VAPID_MISSING_LOG =
  '[Push] Web Push disabled: server VAPID keys are not configured (in-app notifications still work).'

/** iOS PWA ignores requestPermission() without a user gesture; Android Chrome usually allows auto-prompt. */
function isIOSPushClient(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function useFmsNotifications(
  enabled: boolean,
  options?: { suppressToastRef?: MutableRefObject<boolean> }
) {
  const navigate = useNavigate()
  const [items, setItems] = useState<FmsNotificationInboxItem[]>([])
  const [pushEnabled, setPushEnabled] = useState(false)
  const [webPushUiState, setWebPushUiState] = useState<WebPushUiState>('unsupported')
  const [quietArrivalNonce, setQuietArrivalNonce] = useState(0)
  const pushAttempted = useRef(false)
  const vapidMissingLogged = useRef(false)
  /** Browser timers are numeric; avoid NodeJS.Timeout from @types/node. */
  const toastTimers = useRef<Map<number, number>>(new Map())

  const unreadCount = useMemo(() => items.filter(n => !n.read).length, [items])
  const toastItem = useMemo(
    () => items.find(n => n.toastVisible && !n.read) ?? items.find(n => n.toastVisible),
    [items]
  )

  const hideToastOnly = useCallback((id: number) => {
    setItems(prev => prev.map(n => (n.id === id ? { ...n, toastVisible: false } : n)))
  }, [])

  const hideAllToastHighlights = useCallback(() => {
    setItems(prev => prev.map(n => (n.toastVisible ? { ...n, toastVisible: false } : n)))
  }, [])

  const scheduleToastHide = useCallback(
    (id: number) => {
      const existing = toastTimers.current.get(id)
      if (existing) {
        clearTimeout(existing)
      }
      const timer = window.setTimeout(() => {
        toastTimers.current.delete(id)
        hideToastOnly(id)
      }, TOAST_VISIBLE_MS)
      toastTimers.current.set(id, timer)
    },
    [hideToastOnly]
  )

  const markRead = useCallback((id: number, context?: NotificationReadContext) => {
    const t = toastTimers.current.get(id)
    if (t) {
      clearTimeout(t)
      toastTimers.current.delete(id)
    }
    setItems(prev => prev.map(n => (n.id === id ? { ...n, read: true, toastVisible: false } : n)))
    void markNotificationReadOnServer(id, context ?? buildNotificationReadContext('bell_open')).catch(err => {
      console.warn('[Notifications] mark read failed', err)
    })
  }, [])

  const markAllRead = useCallback(
    (readVia: NotificationReadVia = 'bell_mark_all', options?: { skipServer?: boolean }) => {
      toastTimers.current.forEach(t => clearTimeout(t))
      toastTimers.current.clear()
      setItems(prev => prev.map(n => ({ ...n, read: true, toastVisible: false })))
      if (options?.skipServer) {
        return
      }
      void markAllNotificationsReadOnServer(buildNotificationReadContext(readVia)).catch(err => {
        console.warn('[Notifications] mark all read failed', err)
      })
    },
    []
  )

  const dismiss = useCallback(
    (id: number) => {
      markRead(id, buildNotificationReadContext('bell_dismiss'))
    },
    [markRead]
  )

  const openNotification = useCallback(
    (item: FmsNotificationInboxItem) => {
      const path = resolveNotificationPath(item)
      if (path) {
        navigate(path)
      }
      markRead(item.id, buildNotificationReadContext('bell_open'))
    },
    [navigate, markRead]
  )

  const handleIncomingNotification = useCallback(
    (data: FmsNotificationPayload) => {
      if (!data?.id) {
        return
      }
      const suppressToast = Boolean(options?.suppressToastRef?.current)
      setItems(prev => {
        const merged = upsertItem(prev, data, !data.read && !suppressToast)
        if (!data.read && !suppressToast) {
          scheduleToastHide(data.id)
        }
        return merged
      })
      if (!data.read && suppressToast) {
        setQuietArrivalNonce(n => n + 1)
      }
    },
    [scheduleToastHide, options?.suppressToastRef]
  )

  useEffect(() => {
    return () => {
      toastTimers.current.forEach(t => clearTimeout(t))
      toastTimers.current.clear()
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      return
    }

    let cancelled = false
    void fetchNotificationEvents()
      .then(events => {
        if (cancelled) {
          return
        }
        const hydrated = events.map(e => toInboxItem(e, false))
        setItems(prev => mergeInboxItems(prev, hydrated))
      })
      .catch(err => {
        console.warn('[Notifications] failed to load inbox history', err)
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      return
    }

    WebsocketManager.addEventListener(SOCKET_MESSAGES.FMS_NOTIFICATION, handleIncomingNotification)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.FMS_NOTIFICATION, handleIncomingNotification)
    }
  }, [enabled, handleIncomingNotification])

  useEffect(() => {
    if (!enabled || !('serviceWorker' in navigator)) {
      return
    }

    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'fms.push') {
        return
      }
      handleIncomingNotification(event.data.payload as FmsNotificationPayload)
    }

    navigator.serviceWorker.addEventListener('message', onSwMessage)
    return () => {
      navigator.serviceWorker.removeEventListener('message', onSwMessage)
    }
  }, [enabled, handleIncomingNotification])

  const logVapidMissingOnce = useCallback(() => {
    if (vapidMissingLogged.current) {
      return
    }
    vapidMissingLogged.current = true
    console.warn(VAPID_MISSING_LOG)
  }, [])

  const runWebPushSetup = useCallback(async (): Promise<boolean> => {
    if (!enabled || !isPushSupported()) {
      setWebPushUiState('unsupported')
      return false
    }
    try {
      const vapid = await fetchVapidPublicKey()
      if (!vapid.enabled || !vapid.public_key) {
        logVapidMissingOnce()
        setWebPushUiState('unsupported')
        return false
      }
      if (Notification.permission === 'denied') {
        setWebPushUiState('denied')
        return false
      }
      if (Notification.permission === 'default') {
        const perm = await Notification.requestPermission()
        if (perm !== 'granted') {
          setWebPushUiState(perm === 'denied' ? 'denied' : 'prompt')
          return false
        }
      } else if (Notification.permission !== 'granted') {
        setWebPushUiState('denied')
        return false
      }
      await subscribeToWebPush(vapid.public_key)
      setPushEnabled(true)
      setWebPushUiState('enabled')
      return true
    } catch (e) {
      console.warn('[Push] setup skipped', e)
      setWebPushUiState('prompt')
      return false
    }
  }, [enabled, logVapidMissingOnce])

  useEffect(() => {
    if (!enabled || !isPushSupported()) {
      setWebPushUiState('unsupported')
      return
    }
    if (Notification.permission === 'granted') {
      pushAttempted.current = true
      void runWebPushSetup()
      return
    }
    if (Notification.permission === 'denied') {
      setWebPushUiState('denied')
      return
    }
    // iOS PWA: requestPermission() without a tap is often ignored.
    if (isIOSPushClient()) {
      void fetchVapidPublicKey().then(vapid => {
        if (!vapid.enabled || !vapid.public_key) {
          logVapidMissingOnce()
          setWebPushUiState('unsupported')
          return
        }
        setWebPushUiState('prompt')
      })
      return
    }
    if (pushAttempted.current) {
      return
    }
    pushAttempted.current = true
    void runWebPushSetup()
  }, [enabled, runWebPushSetup, logVapidMissingOnce])

  const groupedItems = useMemo(() => {
    const groups = new Map<string, FmsNotificationInboxItem[]>()
    for (const item of items) {
      const key = notificationGroupKey(item)
      const list = groups.get(key) ?? []
      list.push(item)
      groups.set(key, list)
    }
    return Array.from(groups.entries())
  }, [items])

  return {
    items,
    groupedItems,
    unreadCount,
    toastItem,
    dismiss,
    markRead,
    markAllRead,
    openNotification,
    hideAllToastHighlights,
    quietArrivalNonce,
    pushEnabled,
    webPushUiState,
    enableWebPush: runWebPushSetup,
  }
}
